import { LocalizationString } from '@celito.clients/assets';
import {
  ObjectEnum,
  ROUTES,
  TrainingAssignmentObjectKeyEnum,
  TrainingAssignmentTypeEnum,
} from '@celito.clients/enums';
import {
  useCallbackPrompt,
  useLayout,
  useObjectAttributeDefinitions,
} from '@celito.clients/hooks';
import { UserContext } from '@celito.clients/provider';
import {
  downloadTrainingCertificate,
  fetchObjectTaskInfo,
  getAllTaskData,
  updateTaskFields,
} from '@celito.clients/services';
import { useBreadcrumbs } from '@celito.clients/shared';
import { CourseData, TrainingAssignmentData } from '@celito.clients/types';
import { errorToast, raiseErrorToast } from '@celito.clients/utils';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import {
  getAssignmentDetails,
  getCourseDetails,
  startTrainingAssignment,
  updateAssignment,
} from './services';
import { TakingAssingmentProps } from './taking-assingment.model';
import { TakingAssingmetView } from './taking-assingment.view';
import { AssignmentData, DocumentData } from './types';
import { getTaskPayload } from './utils';

export const TakingAssingmentController = (props: TakingAssingmentProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const { configureLayout } = useLayout();
  const { replaceLastBreadcrumb, addBreadcrumb } = useBreadcrumbs();
  const location = useLocation();

  const { user, isBizAdmin } = useContext(UserContext);

  const [isEditing, setIsEditing] = useState(false);
  const [assignmentData, setAssignmentData] = useState<AssignmentData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [dueDate, setDueDate] = useState<Date | string>(new Date());
  const [originalDueDate, setOriginalDueDate] = useState<Date | string | null>(
    null
  );
  const [isDueDateDirty, setIsDueDateDirty] = useState(false);
  const [retrainDate, setRetrainDate] = useState<Date | string | undefined>(
    undefined
  );
  const [trainingAssignmentType, setTrainingAssignmentType] =
    useState<TrainingAssignmentTypeEnum | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [courseData, setCourseData] = useState<CourseData | null>(null);
  const [assignmentDoc, setAssignmentDoc] = useState<DocumentData | undefined>(
    undefined
  );
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const { data: attributes } =
    useObjectAttributeDefinitions<TrainingAssignmentObjectKeyEnum>({
      objectName: ObjectEnum.TRAINING_ASSIGNMENT,
    });

  const abortController = useRef(new AbortController());

  const navigateBack = () => {
    navigate(-1);
  };

  const toggleCancelModalBtnClick = (navigateBack?: boolean) => {
    setIsCancelModalOpen((prev) => !prev);
    if (navigateBack) {
      setIsDueDateDirty(false);
      confirmNavigation();
      navigate(-1);
    }
  };
  const onCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (isDueDateDirty) {
      setIsCancelModalOpen(true);
    } else {
      navigate(-1);
    }
  };

  const getAssignmentDetailsData = useCallback(async () => {
    setIsLoading(true);
    configureLayout({
      pageTitle: '',
      showHeadingLoader: true,
      headerTitle: '',
      enablePadding: false,
    });
    try {
      const assignmentRecordData = await getAssignmentDetails(
        params.name ?? '',
        abortController.current.signal
      );

      getCourseDetailsData(assignmentRecordData);

      if (user?.name === assignmentRecordData.user.name) {
        setTrainingAssignmentType(TrainingAssignmentTypeEnum.MyAssignments);
      }

      getTaskDetailsData(assignmentRecordData);

      setAssignmentData(assignmentRecordData);
      setDueDate(assignmentRecordData.dueDate);
      setOriginalDueDate(assignmentRecordData.dueDate);
      setIsDueDateDirty(false);
      setRetrainDate(
        assignmentRecordData.retrainDate
          ? assignmentRecordData.retrainDate
          : undefined
      );
      configureLayout({
        pageTitle: '',
        headerTitle: assignmentRecordData.label,
        breadcrumbTitle: assignmentRecordData.label,
        enablePadding: false,
      });
    } catch (_error) {
      if (!abortController.current.signal.aborted) {
        raiseErrorToast(_error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [params.name]);

  const getCourseDetailsData = async (
    assignmentRecordData: TrainingAssignmentData
  ) => {
    getCourseDetails(assignmentRecordData, abortController.current.signal)
      .then((courseRecordData) => setCourseData(courseRecordData))
      .catch((_error) => {
        if (!abortController.current.signal.aborted) {
          raiseErrorToast(_error);
        }
      });
  };

  const getTaskDetailsData = async (
    assignmentRecordData: TrainingAssignmentData
  ) => {
    const taskPayload = getTaskPayload(assignmentRecordData?.name ?? '');
    getAllTaskData(taskPayload).then((taskInfo) => {
      const docData =
        taskInfo?.data?.find((task) => !!task?.document)?.document ?? undefined;
      setAssignmentDoc(docData);
    });
  };

  const startTraining = async (
    navigateToMaterials?: boolean,
    updateAssignmentCheck?: boolean
  ) => {
    if (assignmentData) {
      const shouldCallApi =
        user?.name === assignmentData?.user.name ||
        (user?.name !== assignmentData?.user.name && updateAssignmentCheck);

      if (shouldCallApi) {
        try {
          await startTrainingAssignment(assignmentData.name);
          if (!navigateToMaterials) {
            getAssignmentDetailsData();
          }
        } catch (_error) {
          raiseErrorToast(_error);
          return;
        }
      }

      if (navigateToMaterials && courseData?.document) {
        const url = `../${ROUTES.DOCUMENT_VIEW}?training_assignment_name=${assignmentData.name}`;
        navigate(addBreadcrumb({ path: url, label: courseData?.label || '' }));
      }
    }
  };

  const viewCertificate = () => {
    if (assignmentData?.name) {
      setShowExportModal(true);
      downloadTrainingCertificate(assignmentData?.name)
        .catch((_error) => {
          raiseErrorToast(_error);
        })
        .finally(() => {
          setShowExportModal(false);
        });
    } else {
      errorToast({
        message: LocalizationString.ASSIGNMENT_DATA_NOT_FOUND,
      });
    }
  };

  const handleSave = () => {
    if (isEditing) {
      if (assignmentData) {
        if (dueDate) {
          setIsSaving(true);
          updateAssignment({
            name: assignmentData.name,
            isActive: assignmentData.isActive,
            dueDate: new Date(dueDate).toISOString(),
          }).then(() => {
            setIsEditing(false);
            setIsSaving(false);
            getAssignmentDetailsData();
            fetchObjectTaskInfo('training_assignment__a', assignmentData.name, {
              tag: 'ta_take_training',
            }).then((taskData) => {
              if (taskData?.task) {
                const taskName = taskData.task.name;
                updateTaskFields(taskName, {
                  due_date__s: new Date(dueDate).toISOString(),
                });
              }
            });
          });
        } else {
          setIsEditing(true);
          errorToast({
            message: LocalizationString.DATE_FIELD_REQUIRED,
          });
        }
      } else {
        setIsEditing(false);
      }
    } else {
      navigate(
        replaceLastBreadcrumb({
          path: location.pathname,
          label: assignmentData?.label ?? '',
        }),
        { replace: true }
      );
      setIsEditing(true);
    }
  };

  const handleDueDateChange = (date: Date | string | null) => {
    setDueDate(date || new Date());

    // Check if the date has changed from the original
    if (originalDueDate && date) {
      // Extract only year, month, and day for comparison
      const originalDate = new Date(originalDueDate);
      const currentDate = new Date(date);

      // Reset time components to ensure we only compare date parts
      const origYear = originalDate.getFullYear();
      const origMonth = originalDate.getMonth();
      const origDay = originalDate.getDate();

      const currYear = currentDate.getFullYear();
      const currMonth = currentDate.getMonth();
      const currDay = currentDate.getDate();

      setIsDueDateDirty(
        origYear !== currYear || origMonth !== currMonth || origDay !== currDay
      );
    } else {
      // If one is null and the other isn't, it's dirty
      setIsDueDateDirty(!!originalDueDate !== !!date);
    }
  };

  // Set up the navigation prompt
  const { showPrompt, confirmNavigation, cancelNavigation } =
    useCallbackPrompt(isDueDateDirty);

  const cancelEdit = () => {
    if (isDueDateDirty) {
      navigate(-1);
    } else {
      navigate(-1);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    getAssignmentDetailsData();
    const controller = abortController.current;
    return () => {
      controller.abort();
    };
  }, [getAssignmentDetailsData]);

  return (
    <TakingAssingmetView
      {...{
        ...props,
        isBizAdmin,
        isEditing,
        isLoading,
        assignmentData,
        dueDate,
        retrainDate,
        setDueDate: handleDueDateChange,
        handleSave,
        startTraining,
        trainingAssignmentType,
        cancelEdit,
        viewCertificate,
        setShowExportModal,
        showExportModal,
        navigateBack,
        user,
        courseData,
        assignmentDoc,
        isSaving,
        attributes,
        showPrompt,
        confirmNavigation,
        cancelNavigation,
        isDueDateDirty,
        isSaveModalOpen,
        onCancel,
        isCancelModalOpen,
        toggleCancelModalBtnClick,
      }}
    />
  );
};
